import { Avatar, Box, Button, Flex, Text } from '@homebotapp/hb-react-component-catalog'
import { defineMessages, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { selectNewestPrequal } from '../../../../../../store/selectors/prequal'
import { CUSTOMER_MESSAGES } from '../../../../../../constants/customerMessages'
import { useSafeVerificationFlow } from '../../../../../shared/Questions/hooks/useSafeVerificationFlow'
import { VerificationFlowProvider, VerificationFlowManager } from '../../../../../shared/VerificationFlow'
import { useVerificationFlowEnabled } from '../../../../../../hooks/useVerificationFlowEnabled'
import {
  selectPreferredLoanOfficerMembership,
  selectPreferredRealEstateAgentMembership,
  selectPreferredInsuranceAgentMembership
} from '../../../../../../store/selectors/clientTeamMemberships'
import { useCustomerBranding } from '../../../../../../hooks/homebotNetwork/usePreferredAgentOrCustomer'
import { ClientEventSource } from '../../../../../../api/gqlaxy/gql/generated/graphql'
import { ScheduleACallButton } from '../../../../../aiChat/ScheduleACallButton'

const MSG = defineMessages({
  interestedInThisProperty: {
    id: 'insuranceAgentDM.interestedInThisProperty',
    defaultMessage: 'Interested in this property?'
  },
  exploreHomeInsuranceOptions: {
    id: 'insuranceAgentDM.exploreHomeInsuranceOptions',
    defaultMessage: 'Explore home insurance options'
  },
  canHelpYouTakeNextStep: {
    id: 'insuranceAgentDM.canHelpYouTakeNextStep',
    defaultMessage: '{customerFirstName} can help you take the next step so you can plan confidently.'
  },
  getQuote: {
    id: 'insuranceAgentDM.getQuote',
    defaultMessage: '{insuranceAgentFirstName} can get you a quote so you can plan confidently.'
  },
  scheduleACall: {
    id: 'insuranceAgentDM.scheduleACall',
    defaultMessage: 'Schedule a call'
  },
  continueText: {
    id: 'insuranceAgentDM.continueText',
    defaultMessage: "Do you want to continue to {name}'s scheduling site in a new tab?"
  }
})

interface InsuranceAgentDMCardComponentProps {
  listingId?: string
}

const InsuranceAgentDMCardComponent = ({ listingId }: InsuranceAgentDMCardComponentProps) => {
  const intl = useIntl()

  const prequalification = useSelector(selectNewestPrequal)
  const branding = useCustomerBranding('InsuranceAgent')

  const agentOrCustomerPhotoUri = branding?.photoUri
  const agentOrCustomerFirstName = branding?.firstName
  const scheduleACallUri = branding?.appointmentLinkUri
  const preferredInsuranceAgentMembership = useSelector(selectPreferredInsuranceAgentMembership)
  const hasVerifiedInsuranceAgent = preferredInsuranceAgentMembership?.isVerified

  // Use verification flow
  const { startVerification, state: verificationState } = useSafeVerificationFlow()

  const handleExploreHomeInsuranceOptions = async () => {
    // Use verification flow - it handles all fallbacks internally
    if (startVerification && verificationState.currentStep === 'idle') {
      startVerification(
        CUSTOMER_MESSAGES.INSURANCE_CALCULATOR_TO_PREFERRED_INSURANCE_AGENT.message.defaultMessage,
        CUSTOMER_MESSAGES.INSURANCE_CALCULATOR_TO_PREFERRED_INSURANCE_AGENT.topicKey,
        CUSTOMER_MESSAGES.INSURANCE_CALCULATOR_TO_PREFERRED_INSURANCE_AGENT.topicKey,
        'InsuranceAgent',
        listingId ? { listingId } : undefined
      )
    }
  }

  const scheduleACallRenderModalBody = () => {
    return <Text>{intl.formatMessage(MSG.continueText, { name: agentOrCustomerFirstName })}</Text>
  }

  return (
    <Flex flexDirection='column' alignItems='center' bgColor='card-bg-layer-3' p={4} borderRadius={16}>
      <Flex gap={4} alignItems='center'>
        <Avatar mr='16px' src={agentOrCustomerPhotoUri} />
        <Box>
          <Text m={0} color='neutral.900' fontWeight='bold' size='sm'>
            {intl.formatMessage(MSG.interestedInThisProperty)}{' '}
          </Text>
          <Text m={0} color='neutral.900' size='2xs'>
            {hasVerifiedInsuranceAgent
              ? intl.formatMessage(MSG.getQuote, {
                  insuranceAgentFirstName: preferredInsuranceAgentMembership.customerProfile.firstName
                })
              : intl.formatMessage(MSG.canHelpYouTakeNextStep, {
                  customerFirstName: agentOrCustomerFirstName
                })}
          </Text>
        </Box>
      </Flex>
      <Button
        size='sm'
        colorScheme='brand'
        onClick={handleExploreHomeInsuranceOptions}
        mt={4}
        mb={4}
        tracking={{
          guid: '6FEvFvVZp6BQkH1pV4f26i',
          ui_context: 'Listing Details - Home Insurance Breakdown - Insurance Agent DM',
          descriptor: 'Insurance Agent DM Submit Button Clicked'
        }}
      >
        {intl.formatMessage(MSG.exploreHomeInsuranceOptions)}
      </Button>
      {scheduleACallUri && (
        <ScheduleACallButton
          variant='ghost'
          scheduleACall
          size='sm'
          width={['100%', 'auto']}
          px={8}
          color='brand.700'
          eventSource={ClientEventSource.ListingDetailed}
          headerText={intl.formatMessage(MSG.scheduleACall)}
          ctaLinkUrl={scheduleACallUri}
          renderModalBody={scheduleACallRenderModalBody}
          tracking={{
            guid: 'hdWDBkacswWYMCz9v7ih2z',
            ui_context: 'AI Chat CTA',
            descriptor: 'Schedule a call button clicked'
          }}
          _hover={{
            color: 'brand.800 !important',
            backgroundColor: 'neutral.100 !important'
          }}
        >
          {intl.formatMessage(MSG.scheduleACall)}
          <Text color='neutral.900' as='span' ml={1} size='sm'>
            {'with'} {agentOrCustomerFirstName}
          </Text>
        </ScheduleACallButton>
      )}
    </Flex>
  )
}

interface InsuranceAgentDMProps {
  listingId?: string
}

export const InsuranceAgentDM = ({ listingId }: InsuranceAgentDMProps) => {
  const loanOfficerMembership = useSelector(selectPreferredLoanOfficerMembership)
  const realEstateAgentMembership = useSelector(selectPreferredRealEstateAgentMembership)
  const insuranceAgentMembership = useSelector(selectPreferredInsuranceAgentMembership)
  const verificationEnabled = useVerificationFlowEnabled()

  return (
    <VerificationFlowProvider
      options={{
        enableVerificationFlow: verificationEnabled,
        initialPartnerType: 'InsuranceAgent',
        loanOfficerMembership,
        realEstateAgentMembership,
        insuranceAgentMembership,
        includePrequal: false
      }}
    >
      <VerificationFlowManager />
      <InsuranceAgentDMCardComponent listingId={listingId} />
    </VerificationFlowProvider>
  )
}
